/**
 * LLM Keys — admin page for the Bring-Your-Own-Key overrides.
 *
 * Talks to:
 *   GET   /api/admin/llm-keys              (masked view)
 *   PATCH /api/admin/llm-keys              (set / clear)
 *   POST  /api/admin/llm-keys/test         (live validate)
 *   GET   /api/admin/llm-keys/health       (health banner)
 *
 * Design: three provider cards (Anthropic, OpenAI, Gemini). Each card shows the
 * current routing mode (User / Emergent), a masked preview of the stored
 * key (if any), an input for a new key, and Save / Test / Clear buttons.
 */
import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
    KeyRound, Loader2, CheckCircle2, AlertTriangle,
    Save, Trash2, ShieldCheck, ExternalLink,
} from "lucide-react";
import { KeyHealthBanner } from "@/components/llm_keys/KeyHealthBanner";
import { KeyAlertsCard } from "@/components/llm_keys/KeyAlertsCard";
import { ModelPickerCard } from "@/components/llm_keys/ModelPickerCard";
import { SharedNetworkCard } from "@/components/llm_keys/SharedNetworkCard";


const PROVIDERS = [
    {
        key: "anthropic",
        label: "Anthropic (Claude)",
        prefix: "sk-ant-",
        docsUrl: "https://console.anthropic.com/settings/keys",
        pickupNote:
            "Once saved, every Claude call (protocol-ingest, CTA extractor, feature-request AI, flow nodes) routes through YOUR Anthropic account and bills to it directly.",
    },
    {
        key: "openai",
        label: "OpenAI (ChatGPT)",
        prefix: "sk-",
        docsUrl: "https://platform.openai.com/api-keys",
        pickupNote:
            "Once saved, every OpenAI call routes through YOUR account and bills to it directly.",
    },
    {
        key: "gemini",
        label: "Google Gemini",
        prefix: "AQ.",
        docsUrl: "https://aistudio.google.com/app/apikey",
        pickupNote:
            "Once saved, every Gemini call (vision OCR of scanned documents, Gemini agents, CTA / budget / study-analysis fallbacks) goes straight to Google on YOUR account and bills to it directly. Models Google has retired for your account switch automatically to the current model of the same tier (Flash → gemini-flash-latest, Pro → gemini-pro-latest).",
    },
];


export default function LlmKeysPage() {
    const [state, setState] = useState(null);
    const [stats, setStats] = useState(null);
    const [saving, setSaving] = useState(null);   // provider key being saved
    const [testing, setTesting] = useState(null); // provider key being tested
    const [testResult, setTestResult] = useState({});
    const [inputs, setInputs] = useState({ anthropic: "", openai: "", gemini: "" });
    const [health, setHealth] = useState(null);
    const [checking, setChecking] = useState(false);

    const checkHealth = async () => {
        setChecking(true);
        try {
            const { data } = await api.get("/admin/llm-keys/health");
            setHealth(data);
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Key health check failed");
        } finally {
            setChecking(false);
        }
    };

    const refresh = async () => {
        try {
            const [{ data }, { data: statsData }] = await Promise.all([
                api.get("/admin/llm-keys"),
                api.get("/admin/llm-keys/stats?hours=24"),
            ]);
            setState(data);
            setStats(statsData);
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Failed to load LLM keys");
        }
    };
    useEffect(() => {
        refresh();
        checkHealth();
        const t = setInterval(refresh, 60_000); // refresh stats once per minute
        return () => clearInterval(t);
    }, []);

    const saveOne = async (provider) => {
        const val = (inputs[provider] || "").trim();
        if (!val) {
            toast.error("Paste a key first (or use Clear to remove).");
            return;
        }
        setSaving(provider);
        try {
            const body = { [`${provider}_api_key`]: val };
            const { data } = await api.patch("/admin/llm-keys", body);
            setState(data);
            setInputs((prev) => ({ ...prev, [provider]: "" }));
            setTestResult((prev) => ({ ...prev, [provider]: undefined }));
            toast.success(`${labelFor(provider)} key saved — now routing through your account.`);
            checkHealth();
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Save failed");
        } finally {
            setSaving(null);
        }
    };

    const clearOne = async (provider) => {
        if (!window.confirm(
            `Clear the stored ${labelFor(provider)} key and fall back to the Emergent LLM Key?`,
        )) return;
        setSaving(provider);
        try {
            const body = { [`${provider}_api_key`]: "" };
            const { data } = await api.patch("/admin/llm-keys", body);
            setState(data);
            setTestResult((prev) => ({ ...prev, [provider]: undefined }));
            toast.success(`${labelFor(provider)} key cleared — falling back to Emergent.`);
            checkHealth();
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Clear failed");
        } finally {
            setSaving(null);
        }
    };

    const testOne = async (provider) => {
        setTesting(provider);
        try {
            const { data } = await api.post("/admin/llm-keys/test", { provider });
            setTestResult((prev) => ({ ...prev, [provider]: data }));
            if (data.ok) toast.success(`${labelFor(provider)} responded — key is valid.`);
            else toast.error(data.detail || "Test failed");
            checkHealth();
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Test failed");
        } finally {
            setTesting(null);
        }
    };

    if (!state) {
        return <div className="px-8 py-8 text-xs text-white/40 font-mono">Loading LLM key settings…</div>;
    }

    return (
        <div className="px-8 py-8 max-w-5xl" data-testid="llm-keys-page">
            <header className="mb-8">
                <div className="label-xs text-[#7C3AED]">Settings · LLM Providers</div>
                <h1 className="font-display text-4xl tracking-tight mt-2">LLM API Keys</h1>
                <p className="text-sm text-white/55 mt-2 max-w-2xl leading-relaxed">
                    Bring your own Anthropic / OpenAI / Gemini keys so those providers bypass
                    the Emergent LLM proxy and bill directly to your account. Keys are
                    <strong> encrypted at rest</strong> with the system KEK
                    (<code className="text-[11px]">CLIENT_MASTER_KEK</code>) and are
                    never returned to the browser — you&apos;ll only see a masked preview.
                </p>
                <KeyHealthBanner health={health} loading={checking} onRecheck={checkHealth} />
            </header>

            {PROVIDERS.map((p) => (
                <ProviderCard
                    key={p.key}
                    provider={p}
                    entry={state[p.key]}
                    input={inputs[p.key]}
                    setInput={(v) => setInputs((prev) => ({ ...prev, [p.key]: v }))}
                    onSave={() => saveOne(p.key)}
                    onClear={() => clearOne(p.key)}
                    onTest={() => testOne(p.key)}
                    saving={saving === p.key}
                    testing={testing === p.key}
                    testResult={testResult[p.key]}
                />
            ))}

            <ModelPickerCard />
            <KeyAlertsCard onChecked={checkHealth} />
            <SharedNetworkCard />
            <StatsPanel stats={stats} />
        </div>
    );
}


// ─────────────────────────────────────────────────────────
//  24h Route-health panel
//  Shows call volume + spend split by (provider, route), plus the
//  fallback rate so admins can spot recurring vendor issues.
// ─────────────────────────────────────────────────────────
function StatsPanel({ stats }) {
    if (!stats) {
        return (
            <div className="text-[11px] text-white/40 font-mono mt-6" data-testid="llm-stats-loading">
                Loading route health…
            </div>
        );
    }
    const rows = stats.per_route || [];
    return (
        <section className="mt-8 bg-[#0a0a0a] border border-white/10 rounded-2xl p-6"
                 data-testid="llm-stats-panel">
            <header className="flex items-center justify-between mb-4 flex-wrap gap-3">
                <div>
                    <div className="label-xs text-[#7C3AED]">Last {stats.since_hours}h</div>
                    <h2 className="font-display text-xl tracking-tight mt-1">Route health & spend</h2>
                </div>
                <div className="flex items-center gap-6 text-[11px]">
                    <Metric label="Calls" value={stats.total_calls} testId="stat-total-calls" />
                    <Metric label="Spend" value={`$${(stats.total_cost_usd || 0).toFixed(4)}`}
                            testId="stat-total-cost" />
                    <Metric
                        label="Fallback rate"
                        value={`${((stats.fallback_rate || 0) * 100).toFixed(1)}%`}
                        testId="stat-fallback-rate"
                        tone={stats.fallback_rate > 0.05 ? "warn" : "ok"}
                    />
                    <Metric label="Errors" value={stats.error_total || 0}
                            testId="stat-errors"
                            tone={stats.error_total > 0 ? "warn" : "ok"} />
                </div>
            </header>
            {rows.length === 0 ? (
                <div className="text-[12px] text-white/45">
                    No LLM calls in the last {stats.since_hours}h.
                </div>
            ) : (
                <div className="overflow-x-auto">
                    <table className="w-full text-[12px]" data-testid="llm-stats-table">
                        <thead>
                            <tr className="text-left text-[10px] uppercase tracking-wider text-white/45 border-b border-white/10">
                                <th className="py-2 pr-4">Provider</th>
                                <th className="py-2 pr-4">Route</th>
                                <th className="py-2 pr-4 text-right">Calls</th>
                                <th className="py-2 pr-4 text-right">Fallbacks</th>
                                <th className="py-2 pr-4 text-right">Errors</th>
                                <th className="py-2 pr-4 text-right">Tokens in / out</th>
                                <th className="py-2 pr-4 text-right">Avg latency</th>
                                <th className="py-2 pr-4 text-right">Spend</th>
                            </tr>
                        </thead>
                        <tbody>
                        {rows.map((r, i) => (
                            <tr key={i} className="border-b border-white/[0.04]"
                                data-testid={`llm-stats-row-${r.provider}-${r.route}`}>
                                <td className="py-2 pr-4 text-white/85">{r.provider}</td>
                                <td className="py-2 pr-4">
                                    <RouteChip route={r.route} />
                                </td>
                                <td className="py-2 pr-4 text-right font-mono">{r.calls}</td>
                                <td className="py-2 pr-4 text-right font-mono">{r.fallback_used}</td>
                                <td className="py-2 pr-4 text-right font-mono">
                                    <span className={r.errors > 0 ? "text-rose-200" : "text-white/60"}>
                                        {r.errors}
                                    </span>
                                </td>
                                <td className="py-2 pr-4 text-right font-mono text-white/55">
                                    {r.tokens_in.toLocaleString()} / {r.tokens_out.toLocaleString()}
                                </td>
                                <td className="py-2 pr-4 text-right font-mono text-white/55">
                                    {r.latency_ms_avg}ms
                                </td>
                                <td className="py-2 pr-4 text-right font-mono">
                                    ${r.cost_usd.toFixed(4)}
                                </td>
                            </tr>
                        ))}
                        </tbody>
                    </table>
                </div>
            )}
            <div className="text-[10px] text-white/35 mt-3 leading-relaxed">
                Cost is estimated from token counts (chars/4 × per-1K price) — the same
                heuristic the agent runtime uses to bill tenants. Actual vendor invoices
                will match to within a few percent.
            </div>
        </section>
    );
}


function Metric({ label, value, testId, tone }) {
    const toneCls = tone === "warn" ? "text-amber-200"
                  : tone === "ok"   ? "text-emerald-200"
                  : "text-white/85";
    return (
        <div className="text-right" data-testid={testId}>
            <div className="text-[10px] uppercase tracking-wider text-white/45">{label}</div>
            <div className={`text-sm font-mono ${toneCls}`}>{value}</div>
        </div>
    );
}


function RouteChip({ route }) {
    const isUser = route === "user";
    const cls = isUser
        ? "bg-emerald-300/15 text-emerald-200 border-emerald-300/40"
        : route === "custom"
            ? "bg-violet-300/15 text-violet-200 border-violet-300/40"
            : "bg-white/[0.05] text-white/60 border-white/15";
    return (
        <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-md border ${cls}`}>
            {route}
        </span>
    );
}


function labelFor(providerKey) {
    return PROVIDERS.find((p) => p.key === providerKey)?.label || providerKey;
}


function ProviderCard({
    provider, entry, input, setInput,
    onSave, onClear, onTest, saving, testing, testResult,
}) {
    const routingUser = entry?.routing === "user";
    return (
        <section className="bg-[#0a0a0a] border border-white/10 rounded-2xl p-6 mb-5"
                 data-testid={`llm-key-card-${provider.key}`}>
            <header className="flex items-center justify-between gap-3 mb-4 flex-wrap">
                <div className="flex items-center gap-2">
                    <KeyRound className="w-5 h-5 text-[#7C3AED]" strokeWidth={1.5} />
                    <h2 className="font-display text-xl tracking-tight">{provider.label}</h2>
                    <RoutingBadge routing={entry?.routing} />
                </div>
                <div className="flex items-center gap-2">
                    <a href={provider.docsUrl} target="_blank" rel="noopener noreferrer"
                       className="text-[11px] text-violet-200 hover:text-violet-100 inline-flex items-center gap-1">
                        Get a key <ExternalLink className="w-3 h-3" />
                    </a>
                    <button onClick={onTest} disabled={testing || !routingUser}
                            data-testid={`llm-key-test-${provider.key}`}
                            className="px-3 py-1.5 text-xs border border-white/15 hover:bg-white/5 rounded-md inline-flex items-center gap-1.5 disabled:opacity-40">
                        {testing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ShieldCheck className="w-3.5 h-3.5" />}
                        Test key
                    </button>
                </div>
            </header>

            {testResult ? <TestResultBanner result={testResult} /> : null}

            <div className="text-[12px] text-white/55 leading-relaxed mb-4">
                {provider.pickupNote}
            </div>

            {entry?.configured ? (
                <div className="mb-4 flex items-center gap-3 text-xs text-white/55">
                    <span>Current:</span>
                    <span className="font-mono text-white/80" data-testid={`llm-key-mask-${provider.key}`}>
                        {entry.masked}
                    </span>
                    {entry.updated_at ? (
                        <span className="text-[10px] text-white/35">
                            · updated {new Date(entry.updated_at).toLocaleString()}
                            {entry.updated_by ? ` by ${entry.updated_by}` : ""}
                        </span>
                    ) : null}
                </div>
            ) : null}

            <div className="grid grid-cols-12 gap-3 items-start">
                <div className="col-span-12 sm:col-span-9">
                    <label className="block">
                        <span className="text-[11px] uppercase tracking-wider text-white/55 block mb-1.5">
                            {entry?.configured ? "Replace with new key" : "Paste key"}
                        </span>
                        <input type="password" value={input}
                               onChange={(e) => setInput(e.target.value)}
                               placeholder={`${provider.prefix}…`}
                               autoComplete="new-password"
                               data-testid={`llm-key-input-${provider.key}`}
                               className="w-full bg-[#050505] border border-white/10 rounded-md px-3 py-2 text-sm font-mono focus:outline-none focus:border-white/30 focus:ring-2 focus:ring-white/10" />
                    </label>
                </div>
                <div className="col-span-12 sm:col-span-3 flex flex-col gap-2">
                    <button onClick={onSave} disabled={saving || !input.trim()}
                            data-testid={`llm-key-save-${provider.key}`}
                            className="bg-white text-black px-4 py-2 rounded-md text-sm font-medium hover:bg-white/90 disabled:opacity-60 flex items-center gap-2 justify-center">
                        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" strokeWidth={1.8} />}
                        Save
                    </button>
                    {entry?.configured ? (
                        <button onClick={onClear} disabled={saving}
                                data-testid={`llm-key-clear-${provider.key}`}
                                className="px-4 py-2 border border-rose-400/30 text-rose-200 hover:bg-rose-400/10 rounded-md text-sm inline-flex items-center gap-1.5 justify-center">
                            <Trash2 className="w-3.5 h-3.5" /> Clear
                        </button>
                    ) : null}
                </div>
            </div>
        </section>
    );
}


function RoutingBadge({ routing }) {
    const user = routing === "user";
    return (
        <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-md ${
            user ? "bg-emerald-300/15 text-emerald-200 border border-emerald-300/40"
                 : "bg-white/[0.05] text-white/55 border border-white/15"
        }`} data-testid={`llm-key-routing-${routing}`}>
            {user ? "Your account" : "Emergent (fallback)"}
        </span>
    );
}


function TestResultBanner({ result }) {
    const ok = !!result?.ok;
    return (
        <div className={`mb-4 px-3 py-2 rounded-md text-xs inline-flex items-center gap-2 ${
            ok ? "bg-emerald-300/[0.07] border border-emerald-300/30 text-emerald-100"
               : "bg-rose-400/[0.07] border border-rose-400/30 text-rose-100"
        }`} data-testid="llm-key-test-result">
            {ok ? <CheckCircle2 className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
            <span>{result?.detail || (ok ? "OK" : "Failed")}</span>
        </div>
    );
}
